import { LabSubscribeButton, LabSubscribersOnlyCard } from './LabSubscribe.component'
import { Box } from '@mui/material'
import { EditionType } from '../photoStudioTypes'
import { FC } from 'react'
import { GRAY_300 } from 'constants/styling/theme'
import { GenerateButton } from '../common/GenerateButton'
import { LabAiLabelSection } from './LabAiLabelSection.component'
import { LabPanelShell } from './LabPanelPrimitives.component'
import { LabRunSource } from './labRunSource'
import { LabStageOptions } from './LabStageOptions.component'
import { LabStageSettings } from './labSdk.types'
import { isLabStageSettingsValid } from './labSdk.mapping'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { useLabRunSourceOptions } from './useLabRunSourceOptions.hook'
import { useTranslation } from 'react-i18next'
import { useWorkspacePlan } from 'utils/hooks'

interface LabStagePanelProps {
  isDisabled?: boolean
  isProcessingImage?: boolean
  /** `source` is the Generate chevron's pick (After when the main CTA was clicked) */
  onGenerate: (settings: LabStageSettings, source?: LabRunSource) => void
}

/**
 * Stage panel on the Lab SDK engine (FeatureFlag.ALLOW_PHOTO_STUDIO_LAB_SDK): the Stage options (`LabStageOptions`)
 * and the Generate CTA. The settings live in the Studio context, so they survive a tool switch. Non-subscribers get the
 * same panel locked: the Subscribers only card on top, the options disabled, Subscribe in Generate's place.
 */
export const LabStagePanel: FC<LabStagePanelProps> = ({ isDisabled = false, isProcessingImage = false, onGenerate }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()
  const { labStageSettings: settings, updateLabStageSettings } = useClientPhotoStudio()

  const isValid = isLabStageSettingsValid(settings)
  const isGenerateDisabled = isProcessingImage || isDisabled || !isValid
  const disabledTooltip = !isValid ? t('lab.stage.invalid_keep_all') : t('enhance.disabled_generate')
  const sourceOptions = useLabRunSourceOptions((source) => onGenerate(settings, source))
  const isLocked = !hasWorkspaceActiveSubscription

  return (
    <LabPanelShell
      footer={isLocked
        ? <LabSubscribeButton editionType={EditionType.VIRTUAL_STAGING} />
        : (
          <GenerateButton
            onGenerate={() => onGenerate(settings)}
            sourceOptions={sourceOptions}
            disabled={isGenerateDisabled}
            disabledTooltip={isGenerateDisabled ? disabledTooltip : undefined}
            helpPlacement="none"
          />
        )}
    >

      {isLocked && <LabSubscribersOnlyCard margin="0 0 2.4rem" />}

      <LabStageOptions settings={settings} onChange={updateLabStageSettings} disabled={isDisabled || isLocked} />

      {/* AI Label Section (EU AI Act) — staging is where it matters most */}
      <Box sx={{ borderTop: `1px solid ${GRAY_300}`, paddingTop: '1.2rem', marginTop: '2.4rem' }}>
        <LabAiLabelSection isDisabled={isDisabled || isLocked} />
      </Box>

    </LabPanelShell>
  )
}
